import React, { useState, useEffect } from 'react';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import './CategoryFilter.css';

const CategoryFilter = ({ 
  categories, 
  subCategories, 
  selectedCategory, 
  selectedSubCategories = [], 
  onCategoryChange, 
  onSubCategoriesChange,
  isLoading = false 
}) => {
  const [expandedCategories, setExpandedCategories] = useState(new Set());

  // Auto-expand selected category
  useEffect(() => {
    if (selectedCategory && subCategories.length > 0) {
      setExpandedCategories(prev => new Set([...prev, selectedCategory]));
    }
  }, [selectedCategory, subCategories.length]);

  const handleCategoryClick = (category) => {
    if (selectedCategory === category) {
      // If clicking the same category, deselect it
      onCategoryChange('');
      onSubCategoriesChange([]);
    } else {
      // Select new category
      onCategoryChange(category);
      onSubCategoriesChange([]); // Clear subcategories when changing category
      
      // Expand the category to show subcategories
      setExpandedCategories(prev => new Set([...prev, category]));
    }
  };

  const handleSubCategoryClick = (subCategory) => {
    const newSelected = selectedSubCategories.includes(subCategory)
      ? selectedSubCategories.filter(s => s !== subCategory)
      : [...selectedSubCategories, subCategory];
    
    onSubCategoriesChange(newSelected);
  };

  const toggleCategoryExpansion = (category, e) => {
    e.stopPropagation(); // Prevent category selection
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(category)) {
        newSet.delete(category);
      } else {
        newSet.add(category);
      }
      return newSet;
    });
  };

  const isCategoryExpanded = (category) => expandedCategories.has(category);
  const categoryHasSubcategories = (category) => {
    // Assume subcategories are available when a category is selected
    return category === selectedCategory && subCategories.length > 0;
  };

  return (
    <div className="category-filter-sidebar">
      <div className="filter-header">
        <h3>Categories</h3>
      </div>
      
      <div className="category-list">
        {/* All Products option */}
        <div 
          className={`category-item ${!selectedCategory ? 'selected' : ''}`}
          onClick={() => handleCategoryClick('')}
        >
          <span className="category-name">All Products</span>
        </div>

        {/* Category list */}
        {categories.map(category => (
          <div key={category} className="category-group">
            <div 
              className={`category-item ${selectedCategory === category ? 'selected' : ''}`}
              onClick={() => handleCategoryClick(category)}
            >
              <span className="category-name">{category}</span>
              <div className="category-controls">
                {selectedCategory === category && (
                  <button 
                    className="remove-category-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCategoryClick(''); // Deselect category
                    }}
                    aria-label={`Remove ${category} filter`}
                  >
                    ×
                  </button>
                )}
                {categoryHasSubcategories(category) && (
                  <button 
                    className="expand-btn"
                    onClick={(e) => toggleCategoryExpansion(category, e)}
                    disabled={isLoading}
                  >
                    {isCategoryExpanded(category) ? (
                      <ChevronDownIcon className="expand-icon" />
                    ) : (
                      <ChevronRightIcon className="expand-icon" />
                    )}
                  </button>
                )}
              </div>
            </div>
            
            {/* Subcategories */}
            {selectedCategory === category && 
             isCategoryExpanded(category) && 
             subCategories.length > 0 && (
              <div className="subcategory-list">
                {subCategories.map(subCategory => (
                  <div 
                    key={subCategory}
                    className={`subcategory-item ${
                      selectedSubCategories.includes(subCategory) ? 'selected' : ''
                    }`}
                    onClick={() => handleSubCategoryClick(subCategory)}
                  >
                    <span className="subcategory-name">{subCategory}</span>
                    {selectedSubCategories.includes(subCategory) && (
                      <button 
                        className="remove-subcategory-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSubCategoryClick(subCategory); // Toggle off
                        }}
                        aria-label={`Remove ${subCategory} filter`}
                      >
                        ×
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default CategoryFilter;
